/**
 * App chrome. The sidebar is exactly the five `navRoots` from screens.json —
 * no invented global actions — and it collapses under the `lg` breakpoint from
 * design-tokens.json (the media query itself is generated).
 */
import { useEffect, useState, type ReactNode } from 'react'
import { navRoots, type RoutableScreenId, type ScreenId } from '@/lib/contract'
import { t, type CopyKey } from '@/lib/copy'
import { bridge } from '@/lib/bridge'
import { useOrigin } from '@/lib/origin'
import { Link, useBack, useNavigate, useRoute } from '@/lib/router'
import {
  IconBack,
  IconDashboard,
  IconMenu,
  IconProgress,
  IconRecommendations,
  IconSettings,
  IconTransactions,
} from './icons'

const NAV_ICON: Record<string, (props: { size?: number }) => ReactNode> = {
  dashboard: IconDashboard,
  transactions: IconTransactions,
  recommendations: IconRecommendations,
  progress: IconProgress,
  settings: IconSettings,
}

function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const route = useRoute()
  return (
    <>
      <nav className="sidebar" data-open={open} aria-label={t('app.name')}>
        <div className="sidebar-brand">
          <span className="sidebar-mark" aria-hidden="true">
            F
          </span>
          {t('app.name')}
        </div>
        <div className="sidebar-nav">
          {navRoots.map((root) => {
            const Icon = NAV_ICON[root]
            return (
              <Link
                key={root}
                screen={root}
                className="sidebar-link"
                {...(route.screen === root ? { 'aria-current': 'page' as const } : {})}
              >
                {Icon ? <Icon size={20} /> : null}
                {t(`${root}.title` as CopyKey)}
              </Link>
            )
          })}
        </div>
      </nav>
      {open ? <button type="button" className="sidebar-scrim" aria-label={t('cta.close')} onClick={onClose} /> : null}
    </>
  )
}

/**
 * Wraps a screen: reports the screen id (and the state, when the state changes
 * what is rendered) to TransistorKit, and owns the page heading.
 */
export function Screen({
  id,
  state,
  title,
  subtitle,
  actions,
  back,
  chrome = 'app',
  children,
}: {
  id: ScreenId
  state: string
  /** Defaults to the screen's keyed title in copy.json. */
  title?: string
  subtitle?: string
  actions?: ReactNode
  back?: { screen: RoutableScreenId; params?: Record<string, string> }
  chrome?: 'app' | 'standalone'
  children: ReactNode
}) {
  const [navOpen, setNavOpen] = useState(false)
  const route = useRoute()
  const goBack = useBack()
  const heading = title ?? t(`${id}.title` as CopyKey)

  /**
   * The origin is not answering, so serviceUnavailable takes over the BODY of
   * this screen — header and nav stay, and `children` stays mounted (hidden)
   * so its loaders keep their data and stay registered for Retry. Returning is
   * then instant instead of a fresh round of requests.
   */
  const outage = useOrigin()?.interstitial ?? null

  useEffect(() => {
    // Report the interstitial while it is showing, and re-report the screen
    // underneath the moment it clears — so "in serviceUnavailable, do X"
    // addresses the interstitial and not the screen beneath it.
    if (outage) bridge.reportScreen(outage.id, outage.state)
    else bridge.reportScreen(id, state)
  }, [id, state, outage])

  useEffect(() => {
    // An edge failure is not this screen's error: it never enters that state,
    // so it never reports one either.
    if (state === 'error' && !outage) bridge.reportError(`${id} entered its error state`, id)
  }, [id, state, outage])

  useEffect(() => {
    document.title = `${heading} · ${t('app.name')}`
  }, [heading])

  useEffect(() => {
    setNavOpen(false)
  }, [route.screen])

  const header = (
    <header className="page-header">
      {back ? (
        <button type="button" className="back-link" onClick={goBack} aria-label={t('a11y.back')}>
          <IconBack size={18} />
          {t('cta.back')}
        </button>
      ) : null}
      <div className="page-header-text">
        <h1 className="page-title">{heading}</h1>
        {subtitle ? <p className="page-subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  )

  /**
   * Hidden, never unmounted: the screen keeps its state, its fetched data and
   * its registered retries, so Retry re-issues requests instead of being a page
   * reload in everything but name.
   *
   * The wrapper is present in BOTH branches on purpose. Rendering `children`
   * bare when there is no outage and wrapped when there is would move them in
   * the element tree, and React would tear the whole screen down and rebuild it
   * at exactly the moment this code exists to avoid that. `.screen-body` is
   * `display: contents`, so the always-present div costs nothing in layout.
   */
  const body = (
    <>
      <div className="screen-body" hidden={Boolean(outage)}>
        {children}
      </div>
      {outage ? (
        <>
          {/* Standalone screens have no sidebar to carry the wordmark, so the
              interstitial supplies the appTitle that screens.json lists. */}
          {chrome === 'standalone' ? <span className="sidebar-brand">{t('app.name')}</span> : null}
          <outage.Component />
        </>
      ) : null}
    </>
  )

  if (chrome === 'standalone') {
    return (
      <div className="standalone">
        <div className="standalone-inner">{body}</div>
      </div>
    )
  }

  return (
    <div className="app-shell">
      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />
      <main className="main">
        <div className="main-inner">
          <div className="row">
            <button
              type="button"
              className="nav-toggle"
              onClick={() => setNavOpen(true)}
              aria-label={t('a11y.more')}
            >
              <IconMenu />
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>{header}</div>
          </div>
          {body}
        </div>
      </main>
    </div>
  )
}

export function SectionHeader({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="section-header">
      <h2 className="section-title">{title}</h2>
      {action}
    </div>
  )
}

/** A link styled as a "View all" affordance next to a section heading. */
export function ViewAllLink({
  screen,
  params,
  label = t('cta.viewAll'),
}: {
  screen: RoutableScreenId
  params?: Record<string, string>
  label?: string
}) {
  return (
    <Link screen={screen} params={params} className="btn btn-quiet">
      {label}
    </Link>
  )
}

export function useGoTo() {
  return useNavigate()
}
